import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';

/**
 * Klaviyo client-side service.
 * Reads the public `company_id` (safe in the browser) from `window.klaviyoSettings`,
 * which is set per environment in `snippets/klaviyo-settings.liquid` from the
 * `klaviyo_public_api_key` theme setting.
 */
class Klaviyo {
  baseUrl = 'https://a.klaviyo.com';

  requestOptions = {
    method: 'POST',
    headers: {
      accept: 'application/json',
      revision: '2026-01-15',
      'content-type': 'application/json',
    },
  };

  // Public company ID, injected per environment via window.klaviyoSettings.
  get companyId() {
    return window.klaviyoSettings?.companyId;
  }

  /**
   * Create Client Back In Stock Subscription
   * @description Subscribe a profile to receive back in stock notifications. Check out [our Back in Stock API guide](https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock) for more details.
   * @tutorial [Create Client Back-in-Stock Subscription](https://developers.klaviyo.com/en/reference/create_client_profile)
   */
  async createClientBackInStockSubscription({ email, variantId }) {
    if (!variantId) throw new Error('Variant ID is missing');
    if (!this.companyId) throw new Error('Klaviyo company ID is missing (window.klaviyoSettings)');

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(
      `${this.baseUrl}/client/back-in-stock-subscriptions/?company_id=${this.companyId}`,
      { ...this.requestOptions, body: JSON.stringify(payload) }
    );

    // If response is ok and status is 202, return. The response to JSON will be empty and the transforming will fail.
    if (response.ok && response.status === 202) return;

    const data = await response.json();

    // If data.errors?.length, throw an array of errors
    if (data.errors?.length) throw new Error(data.errors[0].title);
    throw new Error('Failed to create back in stock subscription');
  }
}

var Klaviyo$1 = new Klaviyo();

/**
 * Back in stock drawer.
 * A single shared drawer (rendered once in the layout) that subscribes the
 * customer to Klaviyo back-in-stock notifications. The variant to subscribe to
 * is taken from whichever trigger opened it, so the same drawer serves the PDP,
 * the sticky add-to-cart bar and the quick-shop dialog.
 *
 * A trigger is any element carrying:
 *   aria-controls="back-in-stock-drawer" data-variant-id="<id>"
 */
class BackInStockDrawer extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.drawer = this.querySelector('drawer-element');
    this.email = this.querySelector('input[name="email"]');
    this.consent = this.querySelector('input[name="consent"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.successMessage = this.querySelector('[data-back-in-stock-success]');
    this.errorMessage = this.querySelector('[data-back-in-stock-error]');

    this.email.addEventListener('input', this.toggleSubmit);
    this.consent.addEventListener('change', this.toggleSubmit);
    this.form.addEventListener('submit', this.onSubmit);
    this.toggleSubmit();

    // Capture the variant from whichever trigger opens the drawer.
    document.addEventListener('click', this.onTriggerClick);

    // Start each open with a clean form, clearing any previous success/error.
    if (this.drawer) {
      this.unsubscribeDrawerOpened = Pubsub.subscribe(`drawer:opened:${this.drawer.id}`, () =>
        this.reset()
      );
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    this.unsubscribeDrawerOpened?.();
  }

  /**
   * Read the variant id off the trigger that controls this drawer. When the
   * trigger lives inside an open modal dialog (quick-shop), close that dialog
   * first: a <dialog> opened with showModal() sits in the top layer, above the
   * drawer, so the drawer would otherwise open behind it.
   * @param {MouseEvent} event
   */
  onTriggerClick = (event) => {
    const trigger = event.target.closest(`[aria-controls="${this.drawer?.id}"][data-variant-id]`);
    if (!trigger) return;

    this.variantId = trigger.dataset.variantId;

    const openDialog = trigger.closest('dialog[open]');
    if (openDialog) Pubsub.publish('dialog:close', { dialogId: openDialog.id });
  };

  // Enable submit only when the email is valid and consent is checked.
  toggleSubmit = () => {
    this.submitButton.disabled = !(this.email.checkValidity() && this.consent.checked);
  };

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.submitButton.disabled) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('loading');
    this.errorMessage.hidden = true;

    try {
      await Klaviyo$1.createClientBackInStockSubscription({
        email: this.email.value.trim(),
        variantId: this.variantId,
      });
      this.showState('success');
      Pubsub.publish('back-in-stock:subscribed', { variantId: this.variantId });
    } catch (error) {
      console.error(error);
      this.showState('error');
      this.toggleSubmit();
    } finally {
      this.submitButton.classList.remove('loading');
    }
  };

  // Restore the pristine form state (on open). Clearing `loading` matters here:
  // product-form's toggleLoading() targets every submit button on the page, so
  // switching variants leaves this button spinning until the drawer is reopened.
  reset() {
    this.form.reset();
    this.submitButton.classList.remove('loading');
    this.showState('form');
    this.toggleSubmit();
  }

  // Show exactly one of the form / success / error states.
  showState(state) {
    this.form.hidden = state === 'success';
    this.successMessage.hidden = state !== 'success';
    this.errorMessage.hidden = state !== 'error';
  }
}

customElements.define('back-in-stock-drawer', BackInStockDrawer);
